/**
 * LessonEditor - Composant pour modifier une leçon existante
 * Permet de modifier tous les champs et exercices
 */

import React, { useState } from 'react';
import { 
  Save, 
  X, 
  Trash2, 
  Sparkles,
  Loader2,
  Edit3,
} from 'lucide-react';

// Type simplifié pour l'édition
interface EditableLesson {
  title: string;
  subtitle?: string;
  rule?: {
    title?: string;
    rule?: string;
    method?: string;
  };
  tips?: string;
  examples?: any[];
  exercises: Array<{
    id?: string;
    type: string;
    difficulty?: string;
    order?: number;
    text?: string;
    blank_sentence?: string;
    blank_options?: string[];
    options?: string[];
    correct_answer?: string;
    explanation?: string;
  }>;
  difficulty?: string;
  estimated_time?: number;
}

interface LessonEditorProps {
  lesson: EditableLesson;
  onSave: (updatedLesson: EditableLesson) => Promise<void>;
  onGenerateMore: (count: number) => Promise<void>;
  onClose: () => void;
  saving?: boolean;
  generating?: boolean;
}

const LessonEditor: React.FC<LessonEditorProps> = ({
  lesson,
  onSave,
  onGenerateMore,
  onClose,
  saving = false,
  generating = false,
}) => {
  const [editedLesson, setEditedLesson] = useState<EditableLesson>(lesson);
  const [hasChanges, setHasChanges] = useState(false);

  const handleFieldChange = (field: keyof EditableLesson, value: any) => {
    setEditedLesson(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleRuleChange = (field: 'title' | 'rule' | 'method', value: string) => {
    setEditedLesson(prev => ({
      ...prev,
      rule: { ...(prev.rule || {}), [field]: value }
    }));
    setHasChanges(true);
  };

  const handleExerciseChange = (index: number, field: string, value: any) => {
    const newExercises = [...editedLesson.exercises];
    newExercises[index] = { ...newExercises[index], [field]: value };
    setEditedLesson(prev => ({ ...prev, exercises: newExercises }));
    setHasChanges(true);
  };

  const handleDeleteExercise = (index: number) => {
    if (!confirm('Supprimer cet exercice ?')) return;
    const newExercises = editedLesson.exercises.filter((_, i) => i !== index);
    setEditedLesson(prev => ({ ...prev, exercises: newExercises }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(editedLesson);
    setHasChanges(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-xl">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Edit3 className="w-6 h-6" />
              Modifier la leçon
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {editedLesson.exercises.length} exercices • Modifiez le contenu et les exercices
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Titre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titre de la leçon
            </label>
            <input
              type="text"
              value={editedLesson.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Sous-titre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sous-titre / Description
            </label>
            <input
              type="text"
              value={editedLesson.subtitle || ''}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Règle */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg text-gray-800">📖 Règle de grammaire</h3>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Titre de la règle
              </label>
              <input
                type="text"
                value={editedLesson.rule?.title || ''}
                onChange={(e) => handleRuleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Règle principale
              </label>
              <textarea
                value={editedLesson.rule?.rule || ''}
                onChange={(e) => handleRuleChange('rule', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Méthode (avec • pour les bullets)
              </label>
              <textarea
                value={editedLesson.rule?.method || ''}
                onChange={(e) => handleRuleChange('method', e.target.value)}
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Méthode:&#10;• Point 1&#10;• Point 2"
              />
            </div>
          </div>

          {/* Tips */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              💡 Astuces pratiques
            </label>
            <textarea
              value={editedLesson.tips || ''}
              onChange={(e) => handleFieldChange('tips', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Astuces pratiques:&#10;• Astuce 1&#10;• Astuce 2"
            />
          </div>

          {/* Exercices */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-gray-800">
                ✏️ Exercices ({editedLesson.exercises.length})
              </h3>
              <button
                onClick={() => onGenerateMore(5)}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    +5 exercices IA
                  </>
                )}
              </button>
            </div>

            {editedLesson.exercises.map((exercise, index) => (
              <div
                key={exercise.id || index}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700">#{index + 1}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {exercise.type}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {exercise.difficulty}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteExercise(index)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                    title="Supprimer cet exercice"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Question */}
                {exercise.type === 'mcq' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Question</label>
                      <textarea
                        value={exercise.text || ''}
                        onChange={(e) => handleExerciseChange(index, 'text', e.target.value)}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Options (une par ligne)
                      </label>
                      <textarea
                        value={Array.isArray(exercise.options) ? exercise.options.join('\n') : ''}
                        onChange={(e) => handleExerciseChange(index, 'options', e.target.value.split('\n'))}
                        rows={4}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Réponse correcte</label>
                      <input
                        type="text"
                        value={exercise.correct_answer || ''}
                        onChange={(e) => handleExerciseChange(index, 'correct_answer', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}

                {exercise.type === 'fillBlank' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Phrase à trous</label>
                      <textarea
                        value={exercise.blank_sentence || ''}
                        onChange={(e) => handleExerciseChange(index, 'blank_sentence', e.target.value)}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Options (une par ligne)
                      </label>
                      <textarea
                        value={Array.isArray(exercise.blank_options) ? exercise.blank_options.join('\n') : ''}
                        onChange={(e) => handleExerciseChange(index, 'blank_options', e.target.value.split('\n'))}
                        rows={3}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Réponse correcte</label>
                      <input
                        type="text"
                        value={exercise.correct_answer || ''}
                        onChange={(e) => handleExerciseChange(index, 'correct_answer', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Explication */}
                <div className="mt-3">
                  <label className="text-sm font-medium text-gray-600">Explication</label>
                  <textarea
                    value={exercise.explanation || ''}
                    onChange={(e) => handleExerciseChange(index, 'explanation', e.target.value)}
                    rows={2}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 rounded-b-xl">
          <div className="text-sm text-gray-600">
            {hasChanges && (
              <span className="flex items-center gap-2 text-orange-600">
                <Edit3 className="w-4 h-4" />
                Modifications non sauvegardées
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Sauvegarder les modifications
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonEditor;
